import React from 'react';

import {assert, assertAndContinue} from 'Asserts';
import {SampleDetails} from 'constants/SampleConstants';
import collectResultsTablesInNote from 'notebook/integrations/runs/automation-results/collectResultsTablesInNote';
import getSampleDetailsFromNLADResultsTables from 'notebook/integrations/runs/automation-results/getSampleDetailsFromNLADResultsTables';
import collectRunOutputSamplesTablesInNote from 'notebook/integrations/runs/output-samples/collectRunOutputSamplesTablesInNote';
import getSampleDetailsFromRunOutputSamplesTables from 'notebook/integrations/runs/output-samples/GetSampleDetailsFromRunOutputSamplesTable';
import {getRegistrationTableSampleSources} from 'notebook/registration-tables/RegistrationTableOps';
import * as RegistrationTableUtil from 'notebook/registration-tables/RegistrationTableUtil';
import {LegacySamplesSource} from 'notebook/sample-propagation/legacy/legacy-add-samples-modal/LegacyAddSamplesModal';
import {
  collectSamples,
  STRUCTURED_TABLE_TYPES_THAT_MIGHT_HAVE_SAMPLES,
} from 'notebook/sample-propagation/sample-collection/SamplesSourceCollector';
import convertSamplesSourcesToLegacy from 'notebook/sample-propagation/util/convertSamplesSourcesToLegacy';
import makeEmptySampleDetails from 'notebook/sample-propagation/util/makeEmptySampleDetails';
import {getAllCellsOfColumnType} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import collectSomeStructuredTablesInNote from 'notebook/structured-tables/util/collectSomeStructuredTablesInNote';
import {CollectibleLegacyTableType} from 'notebook/structured-tables/util/notePartToTableDatas';
import {Table, TableColumnType} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {getStorableInfosFromTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {StorableInfo} from 'notebook/tables/typed-values/TypedValueTypes';
import {Note} from 'notebook/util/NoteUtilConstants';
import RequestFulfillmentsSampleSourcesCollector from 'requests/RequestFulfillmentsSampleSourcesCollector';

import {isTypeInSampleFilters, SampleFilters, SampleFilterType} from '../SampleFilter';
import filterLegacySampleSources from './filterLegacySampleSources';

/**
 * This component is used to gather samples to pass to the LegacyAddSamples modal.
 *
 * Both the collector and the modal are referred to as "Legacy" because new tables (built on the
 * StructuredTable framework) have a different set of components for both gathering samples and
 * adding them to the table.
 */

interface SamplesSourceCollectorProps {
  note?: Note;
  entryId?: string;
  // If provided, any sample of a type not included in the set will be removed.
  limitSamplesTo?: SampleFilters;
  children: (sampleSources: Array<LegacySamplesSource>) => JSX.Element;
}

function collectGridSampleSources(
  tables: Array<Table>,
  columnDefinitionType: TableColumnType,
  createSampleDetails: (grid: StorableInfo) => SampleDetails
): Array<LegacySamplesSource> {
  const gridSources: Array<LegacySamplesSource> = [];
  for (const table of tables) {
    const tableColumnDefinitions = TableUtil.getColumnDefinitionsFromTable(table);

    // If the user doesn't have permissions to see the table, the table is filtered out from
    // tables already, therefore all tables are expected to have column definition
    tableColumnDefinitions.forEach((columnDefinition) => {
      assert(
        columnDefinition != null,
        'Column definitions are expected to be present is structured tables.',
        {tableColumnDefinitions, tableName: table.name}
      );
    });

    // We then get all of the cells in the grid column, if one exists. The cells are as-is (Cell type)
    const gridCells = getAllCellsOfColumnType({
      columnDefinitionType,
      tableColumnDefinitions,
      table: table.table,
    });

    const sampleDetails: Array<SampleDetails> = [];
    for (const cell of gridCells) {
      // Each grid cell can technically have 0 or more storables. Most likely 1, but not worth an assertion.
      for (const grid of getStorableInfosFromTypedValue(cell.typedValue!)) {
        sampleDetails.push(createSampleDetails(grid));
      }
    }
    if (sampleDetails.length > 0) {
      gridSources.push({
        name: table.name,
        // TODO(BNCH-10676): The icon should be dependent on the table type
        icon: 'table',
        sampleDetails,
      });
    }
  }
  return gridSources;
}

function collectPlateSampleSources(note: Note): Array<LegacySamplesSource> {
  // First we extract all of the tables from the entry that might have plates
  const tablesThatMightHavePlates = collectSomeStructuredTablesInNote(
    note,
    StructuredTableKind.PLATE_CREATION
  ).concat(collectRunOutputSamplesTablesInNote(note));

  return collectGridSampleSources(tablesThatMightHavePlates, TableColumnType.PLATE, (plate) => ({
    ...makeEmptySampleDetails(),
    plateInfo: {
      id: plate.id,
      name: plate.name,
      barcode: plate.barcode,
    },
  }));
}

function collectBoxSampleSources(note: Note): Array<LegacySamplesSource> {
  // First we extract all of the tables from the entry that might have boxes
  const tablesThatMightHaveBoxes = collectSomeStructuredTablesInNote(note, StructuredTableKind.BOX_CREATION);

  return collectGridSampleSources(tablesThatMightHaveBoxes, TableColumnType.BOX, (box) => ({
    ...makeEmptySampleDetails(),
    boxInfo: {
      id: box.id,
      name: box.name,
      barcode: box.barcode,
    },
  }));
}

function collectOtherSampleSources(
  note: Note,
  entryId: string,
  limitSamplesTo?: SampleFilters
): Array<LegacySamplesSource> {
  const sources: Array<LegacySamplesSource> = [
    ...(limitSamplesTo == null || isTypeInSampleFilters(limitSamplesTo, SampleFilterType.PLATE)
      ? collectPlateSampleSources(note)
      : []),
    ...(limitSamplesTo == null || isTypeInSampleFilters(limitSamplesTo, SampleFilterType.BOX)
      ? collectBoxSampleSources(note)
      : []),
    ...getSampleDetailsFromRunOutputSamplesTables(collectRunOutputSamplesTablesInNote(note)),
    ...getSampleDetailsFromNLADResultsTables(collectResultsTablesInNote(note)),
    // TODO(BNCH-12144): Add getSampleDetailsFromRunResultsTables
    ...getRegistrationTableSampleSources(RegistrationTableUtil.getRegistrationTablesInNote(note)),
    ...convertSamplesSourcesToLegacy([
      // Samples in Task tables are _only_ in linked schema fields
      ...collectSamples(note, entryId, [StructuredTableKind.PROCESS_TASK], {skipLinkedSamples: false}),
      // We limit the collection of new structured table samples. Specifically, we don't want to
      // sweep up any tables that would be double-counted.
      ...collectSamples(
        note,
        entryId,
        STRUCTURED_TABLE_TYPES_THAT_MIGHT_HAVE_SAMPLES.filter(
          (type) =>
            ![
              StructuredTableKind.RUN_OUTPUT_SAMPLES,
              StructuredTableKind.PLATE_CREATION,
              StructuredTableKind.PROCESS_TASK,
              CollectibleLegacyTableType.LEGACY_REGISTRATION_TABLE,
            ].includes(type)
        ),
        {skipLinkedSamples: true}
      ),
    ]),
  ];

  return limitSamplesTo ? filterLegacySampleSources(sources, limitSamplesTo) : sources;
}

export default function LegacySamplesSourceCollector(props: SamplesSourceCollectorProps): JSX.Element {
  // We get `note` and `entryId` from the NoteContext provider. These values are nullable to allow
  // NoteContext to be rendered outside an actual note (e.g., in RequestDetails). However, this
  // collector requires a real note, and should only be rendered inside one.
  //
  assertAndContinue(
    props.note != null && props.entryId != null,
    'LegacySamplesSourceCollector should only be rendered inside an entry'
  );

  if (!props.note) {
    return props.children([]);
  }

  return (
    <RequestFulfillmentsSampleSourcesCollector entryId={props.entryId} limitSamplesTo={props.limitSamplesTo}>
      {(sources) =>
        props.children([
          ...collectOtherSampleSources(props.note!, props.entryId!, props.limitSamplesTo),
          ...sources,
        ])
      }
    </RequestFulfillmentsSampleSourcesCollector>
  );
}
